// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import jwtDecode from 'jwt-decode';
import { authAPI } from '../api/auth';
import {
  ACCESS_KEY,
  REFRESH_KEY,
  REFRESH_LOCK_TTL_MS,
  tryAcquireRefreshLock,
  releaseRefreshLock,
  notifyRefreshSuccess,
  notifyRefreshFailure,
} from '../api/axios';

// True for a request that never reached the server (offline, DNS failure,
// timeout) — as opposed to a real rejection (401 on an expired/invalid
// refresh token) where axios still got a response. Only the latter means
// the session is actually invalid.
export function isNetworkError(err) {
  return !err?.response;
}

// Resolves with the new access token once another tab writes one (refresh
// succeeded there), or rejects once it clears both tokens (refresh failed
// there) or the wait times out. Used when this tab couldn't get the refresh
// lock, so its own queued 401 requests still settle instead of hanging.
function waitForOtherTabRefresh(timeoutMs) {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer);
      window.removeEventListener('storage', onStorage);
    };
    const onStorage = (e) => {
      if (e.key !== ACCESS_KEY) return;
      cleanup();
      if (e.newValue) resolve(e.newValue);
      else {
        // The other tab actually cleared tokens — a real session-ending
        // signal, not ambiguous like a timeout. Tag it so the caller can
        // tell the two apart.
        const err = new Error('Session ended in another tab');
        err.sessionEnded = true;
        reject(err);
      }
    };
    const timer = setTimeout(() => {
      cleanup();
      // No storage event arrived at all — could mean the other tab is still
      // offline and hasn't resolved its own refresh yet, not that the
      // session is invalid. Deliberately NOT tagged `sessionEnded`.
      reject(new Error('Timed out waiting for another tab to refresh the session'));
    }, timeoutMs);
    window.addEventListener('storage', onStorage);
  });
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Unique tab ID for cross-tab refresh lock
  const tabId = useRef(`${Date.now()}-${Math.random()}`);
  const isRefreshing = useRef(false);

  // ── Internal: set tokens + decode user ───────────────────────
  const applyTokens = useCallback((access, refresh) => {
    localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
    try {
      const decoded = jwtDecode(access);
      setUser(decoded);
    } catch {
      setUser(null);
    }
  }, []);

  // ── Refresh token ─────────────────────────────────────────────
  const refreshToken = useCallback(async () => {
    if (isRefreshing.current) return;

    if (!tryAcquireRefreshLock(tabId.current)) {
      // Another tab already holds the lock — don't attempt a second refresh.
      // Wait for it to finish instead of bailing: it will write a new access
      // token on success or clear both tokens on failure, and either fires a
      // 'storage' event here. Without this, this tab's own queued 401
      // requests (and this render, while `loading` is still true) would
      // hang until an unrelated event happened to unblock them.
      isRefreshing.current = true;
      try {
        const token = await waitForOtherTabRefresh(REFRESH_LOCK_TTL_MS + 2000);
        try {
          setUser(jwtDecode(token));
        } catch {
          setUser(null);
        }
        notifyRefreshSuccess(token);
      } catch (err) {
        // Only clear the session on the explicit "other tab really logged
        // out" signal — a plain timeout (no storage event arrived) is
        // ambiguous and likely just means still offline, not invalid.
        if (err?.sessionEnded) setUser(null);
        notifyRefreshFailure(err);
      } finally {
        isRefreshing.current = false;
        setLoading(false);
      }
      return;
    }
    isRefreshing.current = true;

    const refresh = localStorage.getItem(REFRESH_KEY);
    if (!refresh) {
      setLoading(false);
      isRefreshing.current = false;
      releaseRefreshLock(tabId.current);
      notifyRefreshFailure(new Error('No refresh token'));
      return;
    }

    try {
      const { data } = await authAPI.refreshToken(refresh);
      applyTokens(data.access, data.refresh || refresh);
      notifyRefreshSuccess(data.access); // ← unblocks queued requests
    } catch (err) {
      // Only a real rejection from the server (expired/invalid/blacklisted
      // refresh token) means the session is actually over. A network error
      // means nothing about the session is known yet — clearing tokens here
      // would silently log the user out just for being offline.
      if (!isNetworkError(err)) {
        localStorage.removeItem(ACCESS_KEY);
        localStorage.removeItem(REFRESH_KEY);
        setUser(null);
      }
      notifyRefreshFailure(err); // ← rejects queued requests
    } finally {
      isRefreshing.current = false;
      releaseRefreshLock(tabId.current);
      setLoading(false);
    }
  }, [applyTokens]);

  // ── On mount: try to restore session ─────────────────────────
  useEffect(() => {
    const access = localStorage.getItem(ACCESS_KEY);
    if (!access) {
      setLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode(access);
      if (decoded.exp * 1000 > Date.now()) {
        setUser(decoded);
        setLoading(false);
      } else {
        // Access expired — try refresh
        refreshToken();
      }
    } catch {
      refreshToken();
    }
  }, [refreshToken]);

  // ── Auto-refresh every 10 minutes ────────────────────────────
  useEffect(() => {
    const interval = setInterval(
      () => {
        if (navigator.onLine && localStorage.getItem(REFRESH_KEY)) refreshToken();
      },
      10 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, [refreshToken]);

  // ── Cross-tab token sync ──────────────────────────────────────
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === ACCESS_KEY) {
        if (e.newValue) {
          try {
            setUser(jwtDecode(e.newValue));
          } catch {
            setUser(null);
          }
        } else {
          setUser(null); // logged out in another tab
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ── listen for interceptor refresh signal ─────────────────
  useEffect(() => {
    const onRefreshRequired = async () => {
      await refreshToken();
      // notifyRefreshSuccess/Failure called inside refreshToken
    };
    window.addEventListener('auth:refresh-required', onRefreshRequired);
    return () =>
      window.removeEventListener('auth:refresh-required', onRefreshRequired);
  }, [refreshToken]);

  // ── Auth actions ──────────────────────────────────────────────
  const login = async (email, password) => {
    try {
      const res = await authAPI.login(email, password);
      // 1. Check if tokens exist before applying
      if (res.data?.access && res.data?.refresh) {
        applyTokens(res.data.access, res.data.refresh);
        return res;
      } else {
        console.error(
          'Login successful but tokens are missing in the response.',
        );
        throw new Error('Tokens missing in response' + res);
      }
    } catch (error) {
      // 2. Handle 401 Unauthorized, 500 Server Error, or Network issues
      console.error(
        'Login failed:',
        error.response?.data?.message || error.message,
      );
      throw error; // the login page shows the message
    }
  };

  const logout = () => {
    authAPI.logout();
    setUser(null);
  };

  const register = (userData) => authAPI.register(userData);

  const updatePassword = (oldPw, newPw) => authAPI.updatePassword(oldPw, newPw);

  const updateProfile = async (data) => {
    const res = await authAPI.updateProfile(data);
    // UserProfileView.patch wraps the updated fields as { message, user }
    const updated = res.data?.user ?? res.data;
    setUser((prev) => ({ ...prev, ...updated }));
    return res.data;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-500">
        Loading…
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        register,
        updatePassword,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
